import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenLink as Link } from './permissions/ScreenLink';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Plus, Search, X } from 'lucide-react';
import { api } from '../services/api';
import StatusBadge from './StatusBadge';
import { apiErrorMessage } from './TimerSummaryModal';
import type { Ticket, TaskRelation, TaskRelationType, TicketStatus } from '../types';
import { toast } from '../store/toastStore';

const RELATION_TYPES: TaskRelationType[] = ['related', 'depends_on', 'blocks'];

/**
 * B.17 — manual task relationships (Related / Depends on / Blocks) in
 * addition to the hierarchy. Staff may add (by search) and remove relations.
 */
export default function TaskRelationsPanel({ ticketId, canEdit, hideWhenEmpty = false, addSignal = 0 }: {
  ticketId: string;
  canEdit: boolean;
  /** Calm by default (controls inventory §4.3): render nothing while there are no relations and no add is open. */
  hideWhenEmpty?: boolean;
  /** Bump to open the add row from outside (the page's ⋯ "קישור למשימה"). */
  addSignal?: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [relationType, setRelationType] = useState<TaskRelationType>('related');
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Opening the add row puts the cursor in its search field.
  useEffect(() => { if (adding) requestAnimationFrame(() => searchRef.current?.focus()); }, [adding]);
  useEffect(() => {
    if (!addSignal) return;
    setAdding(true);
    requestAnimationFrame(() => rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }, [addSignal]);

  const { data, isLoading } = useQuery({
    queryKey: ['ticket-relations', ticketId],
    queryFn: () => api.getTicketRelations(ticketId),
  });
  const relations: TaskRelation[] = data?.data ?? [];

  const { data: searchData, isFetching: searching } = useQuery({
    queryKey: ['ticket-relations-search', search],
    queryFn: () => api.getTickets({ search, limit: 20 }),
    enabled: adding && search.trim().length >= 2,
  });
  const relatedIds = new Set(relations.map((r) => r.other.id));
  const candidates = ((searchData?.data ?? []) as Ticket[]).filter(
    (tk) => tk.id !== ticketId && !relatedIds.has(tk.id)
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ticket-relations', ticketId] });

  const addMutation = useMutation({
    mutationFn: (toId: string) => api.addTicketRelation(ticketId, toId, relationType),
    onSuccess: () => {
      refresh();
      setSearch('');
      setAdding(false);
    },
  });
  const removeMutation = useMutation({
    mutationFn: (relationId: string) => api.removeTicketRelation(ticketId, relationId),
    onSuccess: refresh,
    onError: (err) => toast('error', apiErrorMessage(err) || t('common.deleteError')),
  });

  if (hideWhenEmpty && !adding && !isLoading && relations.length === 0) return null;

  return (
    <div ref={rootRef} className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-surface-900 flex items-center gap-2">
          <Link2 size={16} className="text-accent-text" aria-hidden="true" />
          {t('timeTasks.relations.title')}
        </h3>
        {/* With hideWhenEmpty the host's ⋯ ("קישור למשימה") is the add entry; no second button here. */}
        {canEdit && !adding && !hideWhenEmpty && (
          <button type="button" onClick={() => setAdding(true)} className="btn btn-ghost btn-sm">
            <Plus size={14} aria-hidden="true" /> {t('timeTasks.relations.add')}
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-surface-500">…</p>
      ) : relations.length === 0 ? (
        <p className="text-xs text-surface-500">{t('timeTasks.relations.empty')}</p>
      ) : (
        <ul className="space-y-1.5">
          {relations.map((r) => (
            <li key={r.id} className="dt-row flex items-center gap-2 text-sm">
              <span className="text-xs text-surface-500 w-24 flex-shrink-0">{t(`timeTasks.relations.types.${r.relation_type}`)}</span>
              <Link to={`/tickets/${r.other.id}`} className="flex-1 min-w-0 truncate text-primary-700 hover:underline">
                {r.other.ticket_number ? <span className="text-surface-500 me-1">TKT-{r.other.ticket_number}</span> : null}
                {r.other.title}
              </Link>
              <StatusBadge status={r.other.status as TicketStatus} />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => removeMutation.mutate(r.id)}
                  disabled={removeMutation.isPending}
                  className="dt-reveal btn-icon -m-1 text-surface-500 hover:text-danger-600"
                  aria-label={`${t('timeTasks.relations.remove')}: ${r.other.title}`}
                  title={t('timeTasks.relations.remove')}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3 border-t border-surface-100 pt-3 space-y-2">
          <div className="flex gap-2">
            <select
              value={relationType}
              onChange={(e) => setRelationType(e.target.value as TaskRelationType)}
              className="input text-sm w-36"
              aria-label={t('timeTasks.relations.typeLabel')}
            >
              {RELATION_TYPES.map((rt) => (
                <option key={rt} value={rt}>{t(`timeTasks.relations.types.${rt}`)}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-surface-500" aria-hidden="true" />
              <input
                className="input text-sm w-full ps-9"
                placeholder={t('timeTasks.relations.searchPlaceholder')}
                aria-label={t('timeTasks.relations.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                ref={searchRef}
              />
            </div>
            <button type="button" onClick={() => { setAdding(false); setSearch(''); }} className="btn btn-secondary text-xs">
              {t('common.cancel')}
            </button>
          </div>
          {addMutation.isError && (
            <p className="text-xs text-danger-700">{apiErrorMessage(addMutation.error) || t('timeTasks.relations.addError')}</p>
          )}
          {search.trim().length >= 2 && (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {searching ? (
                <p className="text-xs text-surface-500">…</p>
              ) : candidates.length === 0 ? (
                <p className="text-xs text-surface-500">{t('timeTasks.relations.noResults')}</p>
              ) : (
                candidates.map((tk) => (
                  <button type="button"
                    key={tk.id}
                    onClick={() => addMutation.mutate(tk.id)}
                    disabled={addMutation.isPending}
                    className="w-full text-start px-2 py-1.5 rounded-xs hover:bg-primary-50 text-sm flex items-center gap-2"
                  >
                    <span className="flex-1 truncate">{tk.title}</span>
                    <StatusBadge status={tk.status} />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
